import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { formatCurrency } from "utils";
import { ITireItemDetail } from "features/car_model/types";
import TextFieldComponent from "components/input/TextFieldComponent";
import { MESSAGE } from "constants/validate";
import { Info } from "@mui/icons-material";
import CustomTooltip from "components/CustomTooltip";
import { MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import { TIRE_NUMBER_OPTIONS } from "../constants/options";

const StyledTableCell = styled(TableCell)({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  backgroundColor: "var(--primary-500)",
  width: "calc((100% - 8px) / 5)",
});

const StyledBodyTableCell = styled(TableCell)({
  textAlign: "center",
  width: "210px",
  padding: "11px 20px",
});

const HEADER_FONT_SIZE = { xs: 14, md: 15, lg: 16 };

interface TireListTableProps {
  tireList: ITireItemDetail[];
  tireNumber: number;
  // Keyed by String(tire.id): the server-assigned numeric PK, unique even when
  // item_cd has duplicates. Using id (not index, not item_cd) survives pagination.
  selectedTireIds: Record<string, boolean>;
  cashbackPeriod: number;
  procFees?: MstProcFeeTireItem | null;
  errors: { [key: string]: boolean };
  calculator: (
    tire: ITireItemDetail,
    fee?: MstProcFeeTireItem | null | undefined,
  ) => {
    basePrice: number;
    discountedPrice: number;
    wage: number;
    totalPrice: number;
    compensation: number;
    totalPriceWithCompensation: number;
  };
  onSelectTire: (event: React.ChangeEvent<HTMLInputElement>, tireId: string) => void;
  onTireNumberChange?: (value: number) => void;
  onTireListChange: (tireList: ITireItemDetail[]) => void;
  onErrorChange: (field: string, error: boolean) => void;
  variant?: "default" | "wheel"; // Add variant support
}

/**
 * Component to display tire list table with pricing and selection
 * @param props - Component props
 */
function TireListTable({
  tireList,
  tireNumber,
  selectedTireIds,
  cashbackPeriod,
  procFees,
  errors,
  calculator,
  onSelectTire,
  onTireNumberChange,
  onTireListChange,
  onErrorChange,
  variant = "default",
}: TireListTableProps) {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  return (
    <Box sx={{ overflowX: "scroll", width: "100%", mx: "auto" }}>
      <Table sx={{ minWidth: "1650px" }}>
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ fontSize: HEADER_FONT_SIZE }} rowSpan={2}>
              選択
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              メーカー
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              {variant === "wheel" ? "LI" : "LI/速度記号"}
            </StyledTableCell>
            <StyledTableCell
              sx={{ whiteSpace: "nowrap", px: 1, fontSize: HEADER_FONT_SIZE }}
              rowSpan={2}
            >
              <Box sx={{ display: "flex", flexDirection: "column", rowGap: "18px" }}>
                <Box>本数</Box>
                {variant !== "wheel" && onTireNumberChange && (
                  <FormControl fullWidth>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={String(tireNumber)}
                      onChange={(e) => onTireNumberChange(Number(e.target.value))}
                      label=""
                      sx={{
                        height: "42px",
                        backgroundColor: "var(--white)",
                      }}
                    >
                      {TIRE_NUMBER_OPTIONS.map((num) => (
                        <MenuItem key={num} value={num}>
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </StyledTableCell>
            <StyledTableCell
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
              rowSpan={2}
            >
              通常価格
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              固定割引
            </StyledTableCell>
            <StyledTableCell
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
              rowSpan={2}
            >
              割引後価格
            </StyledTableCell>
            <StyledTableCell
              rowSpan={2}
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
            >
              工賃
            </StyledTableCell>
            <StyledTableCell
              rowSpan={2}
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
            >
              工賃込
              <br />
              価格
            </StyledTableCell>
            <StyledTableCell
              rowSpan={2}
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
            >
              パンク
              <br />
              補償
              <br />
              価格
            </StyledTableCell>
            <StyledTableCell
              rowSpan={2}
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
            >
              パンク
              <br />
              補償込
              <br />
              価格
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              タイヤCB
            </StyledTableCell>
            <StyledTableCell
              sx={{
                whiteSpace: "nowrap",
                textAlign: "left",
                pl: 3,
                pr: "8px",
                fontSize: HEADER_FONT_SIZE,
              }}
            >
              <Box sx={{ position: "relative", pr: "30px" }}>
                店舗 在庫
                <CustomTooltip
                  title="店舗にある在庫数となります。当日販売等により変動するため、販売時は改めて確認してください"
                  arrow
                >
                  <Info sx={{ color: "var(--white)", fontSize: "26px", position: "absolute", right: 0, top: "2px" }} />
                </CustomTooltip>
              </Box>
            </StyledTableCell>
            <StyledTableCell
              rowSpan={2}
              sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}
            >
              YHコード
            </StyledTableCell>
          </TableRow>
          <TableRow>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              商品名
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              ラベリング
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              特別割引
            </StyledTableCell>
            <StyledTableCell sx={{ whiteSpace: "nowrap", fontSize: HEADER_FONT_SIZE }}>
              ホイールセットCB
            </StyledTableCell>
            <StyledTableCell
              sx={{
                whiteSpace: "nowrap",
                textAlign: "left",
                pl: 1.5,
                pr: "8px",
                fontSize: HEADER_FONT_SIZE,
              }}
            >
              <Box sx={{ position: "relative", pr: "30px" }}>
                倉庫/仕入先 在庫
                <CustomTooltip
                  title="倉庫及び仕入先にある在庫数となります。在庫が少ない場合は、必ず確認の上販売してください"
                  arrow
                >
                  <Info sx={{ color: "var(--white)", fontSize: "26px", position: "absolute", right: 0, top: "2px" }} />
                </CustomTooltip>
              </Box>
            </StyledTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {tireList
            .map((tire, originalIndex) => {
              const calculatedPrice = calculator(tire, procFees);
              return { tire, originalIndex, calculatedPrice };
            })
            .filter(({ tire, calculatedPrice }) => calculatedPrice.discountedPrice > 0 || Number(tire.special_discount) >= 100)
            .map(({ tire, originalIndex, calculatedPrice }, displayIndex) => {
              const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
                // Don't toggle if clicking on checkbox or special discount input
                const target = e.target as HTMLElement;
                const isCheckbox = target.closest('input[type="checkbox"]');
                const isSpecialDiscount = target.closest('[data-special-discount]');

                if (!isCheckbox && !isSpecialDiscount) {
                  // Use String(tire.id) — stable unique key across all pages
                  const syntheticEvent = {
                    target: { checked: !(selectedTireIds[String(tire.id)] ?? false) },
                  } as React.ChangeEvent<HTMLInputElement>;
                  onSelectTire(syntheticEvent, String(tire.id));
                }
              };

              const isHovered = hoveredRowIndex === originalIndex;

              return (
                <React.Fragment key={`tire-${tire.id}-${tire.item_cd}`}>
                  <TableRow
                    className={displayIndex % 2 === 0 ? "even" : "odd"}
                    onClick={handleRowClick}
                    onMouseEnter={() => setHoveredRowIndex(originalIndex)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: isHovered ? "var(--silver-100) !important" : undefined,
                    }}
                  >
                    {/* Selection Checkbox */}
                    <StyledBodyTableCell
                      sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}
                      rowSpan={2}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        color="secondary"
                        checked={selectedTireIds[String(tire.id)] ?? false}
                        onChange={(e) => onSelectTire(e, String(tire.id))}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    </StyledBodyTableCell>

                  {/* Maker Name */}
                  <StyledBodyTableCell
                    sx={{
                      textAlign: "left",
                      minWidth: "200px",
                      fontSize: { xs: 16, md: 18, lg: 20 },
                    }}
                  >
                    {tire.maker_name}
                  </StyledBodyTableCell>

                  {/* Speed Mark */}
                  <StyledBodyTableCell
                    sx={{ textAlign: "left", fontSize: { xs: 16, md: 18, lg: 20 } }}
                  >
                    {tire.tire_loin_speed_mark}
                  </StyledBodyTableCell>

                  {/* Tire Number */}
                  <StyledBodyTableCell
                    sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, px: "8px" }}
                    rowSpan={2}
                  >
                    {`${tireNumber}本`}
                  </StyledBodyTableCell>

                  {/* Base Price */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {formatCurrency(calculatedPrice.basePrice)}
                  </StyledBodyTableCell>

                  {/* Fixed Discount */}
                  <StyledBodyTableCell
                    sx={{
                      fontSize:
                        tire.tire_4_price > 0
                          ? { xs: 13, md: 14, lg: 15 }
                          : { xs: 16, md: 18, lg: 20 },
                      fontWeight: tire.tire_4_price > 0 ? "bold" : undefined,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tire.tire_4_price > 0 ? "4本特価" : `${tire.fixed_discount || 0}%`}
                  </StyledBodyTableCell>

                  {/* Discounted Price */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {formatCurrency(calculatedPrice.discountedPrice)}
                  </StyledBodyTableCell>

                  {/* Wage */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {formatCurrency(calculatedPrice.wage)}
                  </StyledBodyTableCell>

                  {/* Total Price */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {formatCurrency(calculatedPrice.totalPrice)}
                  </StyledBodyTableCell>

                  {/* Compensation */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {formatCurrency(calculatedPrice.compensation)}
                  </StyledBodyTableCell>

                  {/* Total with Compensation */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {formatCurrency(calculatedPrice.totalPriceWithCompensation)}
                  </StyledBodyTableCell>

                  {/* Cashback */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "18px",
                        textAlign: "left",
                        padding: "0 20px",
                      }}
                    >
                      <Box>
                        -
                        {tire.cb_tire_price && cashbackPeriod === 2
                          ? formatCurrency(tire.cb_tire_price)
                          : ""}
                      </Box>
                      <Box>
                        -
                        {tire.cb_wheel_price && cashbackPeriod === 2
                          ? formatCurrency(tire.cb_wheel_price)
                          : ""}
                      </Box>
                    </Box>
                  </StyledBodyTableCell>

                  {/* Stock */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "18px",
                        justifyContent: "space-evenly",
                        alignItems: "center",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box>
                        {tire.store_quantity != null ? tire.store_quantity : ""}
                      </Box>
                      <Box>
                        {tire.depot_quantity != null || tire.vendor_centor_quantity != null
                          ? `${tire.depot_quantity ?? ""} / ${tire.vendor_centor_quantity ?? ""}`
                          : ""}
                      </Box>
                    </Box>
                  </StyledBodyTableCell>

                  {/* Item Code */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }} rowSpan={2}>
                    {tire.item_cd}
                  </StyledBodyTableCell>
                </TableRow>

                {/* Second Row */}
                <TableRow
                  className={displayIndex % 2 === 0 ? "even" : "odd"}
                  onClick={handleRowClick}
                  onMouseEnter={() => setHoveredRowIndex(originalIndex)}
                  onMouseLeave={() => setHoveredRowIndex(null)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: isHovered ? "var(--silver-100) !important" : undefined,
                  }}
                >
                  {/* Item Name */}
                  <StyledBodyTableCell
                    sx={{ textAlign: "left", fontSize: { xs: 16, md: 18, lg: 20 } }}
                  >
                    {tire.item_name}
                  </StyledBodyTableCell>

                  {/* Tire Label */}
                  <StyledBodyTableCell
                    sx={{ textAlign: "left", fontSize: { xs: 16, md: 18, lg: 20 } }}
                  >
                    {tire.tire_label}
                  </StyledBodyTableCell>

                  {/* Special Discount */}
                  <StyledBodyTableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>
                    <Box
                      data-special-discount
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-evenly",
                      }}
                    >
                      <TextFieldComponent
                        type="text"
                        value={tire.special_discount ?? ""}
                        inputProps={{ maxLength: 2, inputMode: "numeric" }}
                        sxInput={{ fontSize: { xs: 16, md: 18, lg: 20 }, height: "42px" }}
                        sxRoot={{
                          borderRadius: 0,
                          width: "100%",
                          background: tire.tire_4_price > 0 ? "var(--silver-250)" : "inherit",
                          border: !errors[`${originalIndex}-special-discount`]
                            ? "2px solid var(--silver-250)"
                            : "2px solid var(--red-800)",
                        }}
                        disabled={tire.tire_4_price > 0}
                        normalizeHalfWidth
                        sxNotchedOutline={{ border: "none" }}
                        onInputText={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                          const empty = "" as unknown as ITireItemDetail["special_discount"];
                          let special_discount: ITireItemDetail["special_discount"];
                          if (digits === "") {
                            special_discount = empty;
                          } else {
                            const n = parseInt(digits, 10);
                            special_discount = n === 0 ? empty : n;
                          }
                          const updatedTire = {
                            ...tire,
                            special_discount,
                          };
                          const updatedTireList = tireList.map((t) =>
                            t.id === tire.id ? updatedTire : t,
                          );
                          onTireListChange(updatedTireList);
                        }}
                        isHiddenError
                        validateOption={{
                          maxLength: {
                            message: MESSAGE.MAX_LENGTH(2),
                            value: 2,
                          },
                          halfWidth: {
                            message: MESSAGE.HALFWIDTH,
                          },
                        }}
                        onError={(error) => {
                          onErrorChange(`${originalIndex}-special-discount`, error);
                          if (!error) {
                            const updatedErrors = { ...errors };
                            delete updatedErrors[`${originalIndex}-special-discount`];
                            onErrorChange(`${originalIndex}-special-discount`, false);
                          }
                        }}
                      />
                      <Box ml="2">%</Box>
                    </Box>
                  </StyledBodyTableCell>
                </TableRow>
              </React.Fragment>
              );
            })}
        </TableBody>
      </Table>
    </Box>
  );
}

export default TireListTable;
